import React, { useState, useEffect, useRef, useCallback } from 'react';
import "../styles/map.css"

// Constants for better maintainability
const MAP_CONFIG = {
  DEFAULT_CENTER: { lat: 25.704107970077455, lng: 94.10712021211401 },
  DEFAULT_ZOOM: 12,
  MARKER_ZOOM: 15,
  DEBOUNCE_DELAY: 1000,
  SCRIPT_LOAD_DELAY: 100,
  MARKER_ICON: {
    url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
    size: { width: 40, height: 40 },
    anchor: { x: 20, y: 40 }
  }
};

const GoogleMap = React.memo(({ onLocationSelect, selectedCoords, searchLocation }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);

  // Use refs to prevent unnecessary re-renders and dependencies
  const onLocationSelectRef = useRef(onLocationSelect);
  const selectedCoordsRef = useRef(selectedCoords);
  const searchLocationRef = useRef(searchLocation);

  // Update refs when props change - minimal effect
  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
    selectedCoordsRef.current = selectedCoords;
    searchLocationRef.current = searchLocation;
  });

  // Check if Google Maps is already loaded
  const isGoogleMapsLoaded = useCallback(() => {
    return window.google && window.google.maps && window.google.maps.Map;
  }, []);

  // Initialize map once Google Maps is ready
  const initializeMap = useCallback(() => {
    if (!mapRef.current) {
      console.error('Map container not found');
      return;
    }

    if (!isGoogleMapsLoaded()) {
      setMapError('Google Maps API not available');
      return;
    }

    try {
      const { google } = window;
      const defaultCenter = selectedCoordsRef.current || MAP_CONFIG.DEFAULT_CENTER;

      // Create map instance
      const map = new google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: MAP_CONFIG.DEFAULT_ZOOM,
        zoomControl: true,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        draggable: true,
      });

      mapInstanceRef.current = map;
      geocoderRef.current = new google.maps.Geocoder();

      // Add click listener for map
      map.addListener('click', (event) => {
        const location = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        updateMarker(location);
        onLocationSelectRef.current(location);
      });

      // Set initial marker
      const initialLocation = selectedCoordsRef.current || defaultCenter;
      updateMarker(initialLocation);
      
      if (!selectedCoordsRef.current) {
        onLocationSelectRef.current(initialLocation);
      }

      setMapLoaded(true);
      setMapError(null);

    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError('Failed to initialize map. Please try again.');
    }
  }, [isGoogleMapsLoaded]);

  // Update marker position with optimized creation
  const updateMarker = useCallback((location) => {
    if (!mapInstanceRef.current || !isGoogleMapsLoaded()) return;

    const { google } = window;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    markerRef.current = new google.maps.Marker({
      position: location,
      map: mapInstanceRef.current,
      title: 'Selected Location - Drag me!',
      draggable: true,
      animation: google.maps.Animation.DROP,
      icon: {
        url: MAP_CONFIG.MARKER_ICON.url,
        scaledSize: new google.maps.Size(
          MAP_CONFIG.MARKER_ICON.size.width,
          MAP_CONFIG.MARKER_ICON.size.height
        ),
        anchor: new google.maps.Point(
          MAP_CONFIG.MARKER_ICON.anchor.x,
          MAP_CONFIG.MARKER_ICON.anchor.y
        ),
      }
    });

    // Add dragend event
    markerRef.current.addListener('dragend', (event) => {
      const newLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      
      // Add bounce animation
      markerRef.current.setAnimation(google.maps.Animation.BOUNCE);
      setTimeout(() => {
        if (markerRef.current) {
          markerRef.current.setAnimation(null);
        }
      }, 1400);

      onLocationSelectRef.current(newLocation);
    });

    // Center and zoom map
    mapInstanceRef.current.panTo(location);
    mapInstanceRef.current.setZoom(MAP_CONFIG.MARKER_ZOOM);
  }, [isGoogleMapsLoaded]);

  // Handle search location with proper cleanup
  const handleSearchLocation = useCallback(() => {
    if (!searchLocationRef.current || 
        !mapInstanceRef.current || 
        !mapLoaded || 
        !geocoderRef.current ||
        !isGoogleMapsLoaded()) {
      return;
    }

    geocoderRef.current.geocode({ 
      address: searchLocationRef.current 
    }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const newLocation = {
          lat: location.lat(),
          lng: location.lng()
        };
        updateMarker(newLocation);
        onLocationSelectRef.current(newLocation);
      }
    });
  }, [mapLoaded, updateMarker, isGoogleMapsLoaded]);

  // Load Google Maps API - runs only once
  useEffect(() => {
    // If already loaded, initialize immediately
    if (isGoogleMapsLoaded()) {
      initializeMap();
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkInterval = setInterval(() => {
        if (isGoogleMapsLoaded()) {
          clearInterval(checkInterval);
          initializeMap();
        }
      }, MAP_CONFIG.SCRIPT_LOAD_DELAY);
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setTimeout(() => {
        if (isGoogleMapsLoaded()) {
          initializeMap();
        }
      }, MAP_CONFIG.SCRIPT_LOAD_DELAY);
    };
    
    script.onerror = () => {
      console.error('Failed to load Google Maps');
      setMapError('Failed to load Google Maps. Please check your internet connection.');
    };
    
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Clean up map instance to prevent memory leaks
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, [initializeMap, isGoogleMapsLoaded]);

  // Handle search location changes with debouncing
  useEffect(() => {
    if (!searchLocation || !mapLoaded) return;

    const timeoutId = setTimeout(() => {
      handleSearchLocation();
    }, MAP_CONFIG.DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [searchLocation, mapLoaded, handleSearchLocation]);

  // Handle selected coordinates changes
  useEffect(() => {
    if (selectedCoords && mapInstanceRef.current && mapLoaded) {
      updateMarker(selectedCoords);
    }
  }, [selectedCoords, mapLoaded, updateMarker]);

  return (
    <div className="map-container">
      {/* Error State */}
      {mapError && (
        <div className="map-error">
          <p>❌ {mapError}</p>
        </div>
      )}
      
      {/* Loading State */}
      {!mapLoaded && !mapError && (
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>Loading map...</p>
        </div>
      )}

      {/* Map Container */}
      <div 
        ref={mapRef} 
        className="google-map"
        style={{ 
          width: '100%', 
          height: '100%',
          display: mapLoaded ? 'block' : 'none'
        }}
      />
    </div>
  );
});

// Custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.onLocationSelect === nextProps.onLocationSelect &&
    prevProps.selectedCoords?.lat === nextProps.selectedCoords?.lat &&
    prevProps.selectedCoords?.lng === nextProps.selectedCoords?.lng &&
    prevProps.searchLocation === nextProps.searchLocation
  );
};

export default React.memo(GoogleMap, arePropsEqual);